import React, { useState, useEffect, useLayoutEffect } from 'react';
import {
  getMediaStart,
  removeChat,
  collapseIframe,
  expandIframe,
} from '../../../helpers/helpers';
import { ParamsNotFoundError } from '../../../helpers/definedErrors';
import { config } from '../../../config';
import { ReactChat } from '@smarterservices/react-chat';
import {
  Card,
  CloseButton,
  IconArrowOpenUpLine,
  IconButton,
  IconArrowOpenDownLine,
} from '@smarterservices/smarter-ui';

const useWindowHeight = () => {
  const [windowHeight, setWindowHeight] = useState(0);

  useLayoutEffect(() => {
    // Function to update the height state
    const updateHeight = () => {
      setWindowHeight(window.innerHeight);
    };

    // Set initial height
    updateHeight();

    // Add event listener for window resize
    window.addEventListener('resize', updateHeight);

    // Clean up event listener on unmount
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  return windowHeight;
};

const ProctorChat = () => {
  const [token, setToken] = useState(null);
  const [roomId, setRoomId] = useState(null);
  const [identity, setIdentity] = useState(null);
  const [referenceId, setReferenceId] = useState(null);
  const [websocketUrl, setWebsocketUrl] = useState(null);
  const [displayName, setDisplayName] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const windowHeight = useWindowHeight();
  // Card header has a fixed height of 42px

  useEffect(() => {
    const initChat = async () => {
      // There were no params stored, so we need to get them from the URL
      const url = new URL(window.location.href);
      const chatParams = new Proxy(new URLSearchParams(url.search), {
        get: (searchParams, prop) => searchParams.get(prop),
      });
      if (
        !chatParams ||
        !chatParams.token ||
        !chatParams.roomId ||
        !chatParams.identity ||
        !chatParams.referenceId ||
        !chatParams.isExpanded
      ) {
        throw new ParamsNotFoundError();
      }

      // Get the user's display name and the websocket url to add to the params
      const response = await getMediaStart(
        chatParams.identity,
        chatParams.token
      );

      setIdentity(chatParams.identity);
      setToken(chatParams.token);
      setRoomId(chatParams.roomId);
      setReferenceId(chatParams.referenceId);

      // Set expanded state and collapse iframe if needed
      const isExpanded = chatParams.isExpanded === 'true';
      setIsExpanded(isExpanded);

      if (!isExpanded) {
        collapseIframe('spProctorChatIframeId');
      }

      setWebsocketUrl(response.websocketUrl);
      setDisplayName(response.displayName);
    };
    initChat();
  }, []);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
    if (isExpanded) {
      collapseIframe('spProctorChatIframeId');
    } else {
      expandIframe('spProctorChatIframeId');
    }
  };

  const HEADER_HEIGHT = 50;
  const bodyHeight =
    windowHeight > 0 ? `${windowHeight - HEADER_HEIGHT}px` : '100vh';

  return identity &&
    token &&
    roomId &&
    referenceId &&
    websocketUrl &&
    displayName ? (
    <Card
      background="secondary"
      borderColor="brand"
      borderWidth="large"
      borderRadius="x-small"
    >
      <Card.Header background="brand">
        <Card.Title>Proctor Chat</Card.Title>
        <Card.Actions>
          {isExpanded ? (
            <IconButton
              withBackground={false}
              withBorder={false}
              margin="small"
              size="small"
              color="primary-inverse"
              screenReaderLabel="Collapse"
              focusColor="inverse"
              onClick={toggleExpand}
            >
              <IconArrowOpenUpLine />
            </IconButton>
          ) : (
            <IconButton
              withBackground={false}
              withBorder={false}
              margin="small"
              size="small"
              color="primary-inverse"
              screenReaderLabel="Expand"
              focusColor="inverse"
              onClick={toggleExpand}
            >
              <IconArrowOpenDownLine />
            </IconButton>
          )}
          <CloseButton
            color="primary-inverse"
            screenReaderLabel="Close"
            onClick={removeChat}
          />
        </Card.Actions>
      </Card.Header>
      {isExpanded && (
        <Card.Body padding="0" height={bodyHeight}>
          <ReactChat
            roomId={roomId}
            identity={identity}
            token={token}
            referenceId={referenceId}
            apiUrl={config.proctoring.api.baseUrl}
            websocketUrl={websocketUrl}
            displayName={displayName}
          />
        </Card.Body>
      )}
    </Card>
  ) : null;
};

export default ProctorChat;
